import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { profileService, type CuppingProfile } from '../../services/profileService';
import { getSCAScore } from '../../pages/inventory/components/ScaScoreMapping';
import { useLanguage } from '../../features/language/useLanguage';

export interface CuppingFormData {
  cuppingType: 'Full' | 'Simplified' | 'SCA';
  cuppingDate: string;
  roastLevel: number;
  qcPassed: boolean | null;
  defects: string[];
  fragrance_aroma: number;
  fragrance: number;
  aroma: number;
  flavor: number;
  aftertaste: number;
  acidity: number;
  sweetness: number;
  mouthfeel: number;
  body: number;
  balance: number;
  overall: number;
  notes_fragrance: string;
  notes_flavor: string;
  notes_acidity: string;
  notes_sweetness: string;
  notes_mouthfeel: string;
  notes_body?: string;
  fragrance_notes_cata: string[];
  flavor_notes_cata: string[];
  main_tastes: string[];
  mouthfeel_attributes: string[];
  acidity_attributes: string[];
  roast_defect_underdevelopment: number;
  roast_defect_overdevelopment: number;
  roast_defect_baked: number;
  roast_defect_scorched: number;
  sca_non_uniform_cups?: number;
  sca_defective_cups?: number;
  sca_final_score?: number;
}

export interface CuppingProfileFormProps {
  initialData?: Partial<CuppingProfile>;
  onSuccess?: () => void;
  onCancel?: () => void;
  lotId?: string;
  entityType?: 'ItemType' | 'LotType' | 'SampleType';
  entityId?: string;
  readOnly?: boolean;
  sessionId?: string;
}

export const useCuppingProfileForm = ({
  initialData,
  onSuccess,
  lotId,
  entityType,
  entityId,
  sessionId
}: CuppingProfileFormProps) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'Full' | 'Simplified' | 'SCA'>(
    (initialData?.cupping_type as 'Full' | 'Simplified' | 'SCA') || 'Simplified'
  );
  const isFullMode = mode === 'Full';

  const defaultValues = useMemo(() => {
    const base = initialData || {};
    const simplified = (base as { cupping_simplified?: Record<string, unknown> }).cupping_simplified || {};
    const full = (base as { cupping_full?: Record<string, unknown> }).cupping_full || {};
    const sca = (base as { cupping_sca?: Record<string, unknown> }).cupping_sca || {};
    const activeData = mode === 'Full' ? full : mode === 'Simplified' ? simplified : sca;
    const extendedData = base.extended_data || {};
    const modeKey = mode === 'Full' ? 'cupping_full' : mode === 'Simplified' ? 'cupping_simplified' : 'sca';
    const modeData = (extendedData[modeKey] || {}) as Record<string, unknown>;

    const getVal = (key: string, legacyVal: unknown, defaultVal: unknown) => {
      if (modeData[key] !== undefined) return modeData[key];
      return legacyVal !== undefined ? legacyVal : defaultVal;
    };

    return {
      cuppingType: mode,
      cuppingDate: base.cupping_date ? base.cupping_date.split('T')[0] : new Date().toISOString().split('T')[0],
      roastLevel: base.roast_level || 3,
      qcPassed: base.is_qc_passed !== undefined ? base.is_qc_passed : null,
      defects: base.defects || [],
      fragrance_aroma: getVal('fragrance_aroma', base.fragrance_aroma, 1),
      fragrance: getVal('fragrance', base.fragrance, 1),
      aroma: getVal('aroma', base.aroma, 1),
      flavor: getVal('flavor', base.flavor, 1),
      aftertaste: getVal('aftertaste', base.aftertaste, 1),
      acidity: getVal('acidity', base.acidity, 1),
      sweetness: getVal('sweetness', base.sweetness, 1),
      mouthfeel: getVal('mouthfeel', base.mouthfeel, 1),
      body: getVal('body', base.body, 1),
      balance: getVal('balance', base.balance, 1),
      overall: getVal('overall', base.overall, 1),
      notes_fragrance: getVal('notes_fragrance', activeData.notes_fragrance, ''),
      notes_flavor: getVal('notes_flavor', activeData.notes_flavor, ''),
      notes_acidity: getVal('notes_acidity', activeData.notes_acidity, ''),
      notes_sweetness: getVal('notes_sweetness', activeData.notes_sweetness, ''),
      notes_mouthfeel: getVal('notes_mouthfeel', activeData.notes_mouthfeel, ''),
      notes_body: getVal('notes_body', activeData.notes_body, ''),
      fragrance_notes_cata: getVal('fragrance_notes_cata', activeData.fragrance_notes_cata, []),
      flavor_notes_cata: getVal('flavor_notes_cata', activeData.flavor_notes_cata, []),
      main_tastes: getVal('main_tastes', activeData.main_tastes, []),
      mouthfeel_attributes: getVal('mouthfeel_attributes', activeData.mouthfeel_attributes, []),
      acidity_attributes: getVal('acidity_attributes', activeData.acidity_attributes, []),
      roast_defect_underdevelopment: Math.max(1, Number(getVal('roast_defect_underdevelopment', base.roast_defect_underdevelopment, 1) || 1)),
      roast_defect_overdevelopment: Math.max(1, Number(getVal('roast_defect_overdevelopment', base.roast_defect_overdevelopment, 1) || 1)),
      roast_defect_baked: Math.max(1, Number(getVal('roast_defect_baked', base.roast_defect_baked, 1) || 1)),
      roast_defect_scorched: Math.max(1, Number(getVal('roast_defect_scorched', base.roast_defect_scorched, 1) || 1)),
      sca_non_uniform_cups: Number(getVal('non_uniform_cups', 0, 0)),
      sca_defective_cups: Number(getVal('defective_cups', 0, 0)),
      sca_final_score: Number(getVal('final_score', base.score, 0)),
    } as CuppingFormData;
  }, [initialData, mode]);

  const { register, control, handleSubmit, watch, setValue, getValues, formState } = useForm<CuppingFormData>({
    defaultValues
  });

  const maxScore = mode === 'Full' ? 15 : mode === 'Simplified' ? 5 : 9;

  useEffect(() => {
    if (mode === 'Simplified') {
      const currentValues = getValues();
      const scoreFields: (keyof CuppingFormData)[] = [
        'fragrance_aroma',
        'flavor',
        'aftertaste',
        'acidity',
        'sweetness',
        'body',
        'balance',
        'overall',
        'roast_defect_underdevelopment',
        'roast_defect_overdevelopment',
        'roast_defect_baked',
        'roast_defect_scorched',
      ];

      scoreFields.forEach(field => {
        const val = currentValues[field];
        if (typeof val === 'number' && val > 5) {
          setValue(field, 5);
        }
      });

      setValue('mouthfeel_attributes', []);
    }
  }, [mode, setValue, getValues]);

  const scaWatched = useWatch({
    control,
    name: [
      'fragrance',
      'aroma',
      'flavor',
      'aftertaste',
      'acidity',
      'body',
      'sweetness',
      'overall',
      'sca_non_uniform_cups',
      'sca_defective_cups',
    ],
  });

  const scaComputedFinalScore = useMemo(() => {
    if (mode !== 'SCA') return undefined;
    const [
      fragrance, aroma, flavor, aftertaste, acidity, body, sweetness, overall, nonU, defect
    ] = scaWatched.map(Number);
    const aSum =
      (fragrance || 0) +
      (aroma || 0) +
      (flavor || 0) +
      (aftertaste || 0) +
      (acidity || 0) +
      (body || 0) +
      (sweetness || 0) +
      (overall || 0);
    const base = getSCAScore(aSum);
    const penalties = (nonU || 0) * 2 + (defect || 0) * 4;
    return Math.max(0, Math.min(100, +(base - penalties).toFixed(2)));
  }, [mode, scaWatched]);

  const handleSave = async (data: CuppingFormData) => {
    try {
      if (mode === 'SCA') {
        if ((Number(data.sca_defective_cups) || 0) > 0 && (!data.defects || data.defects.length === 0)) {
          alert(t('cuppingProfileForm.errors.selectDefectTypeWhenDefectiveCups'));
          return;
        }
        const aSum =
          Number(data.fragrance || 0) +
          Number(data.aroma || 0) +
          Number(data.flavor || 0) +
          Number(data.aftertaste || 0) +
          Number(data.acidity || 0) +
          Number(data.body || 0) +
          Number(data.sweetness || 0) +
          Number(data.overall || 0);
        const base = getSCAScore(aSum);
        const nonUniform = Number(data.sca_non_uniform_cups || 0);
        const defective = Number(data.sca_defective_cups || 0);
        const penalties = nonUniform * 2 + defective * 4;
        const finalScore = Math.max(0, Math.min(100, +(base - penalties).toFixed(2)));

        const payload: Omit<CuppingProfile, 'id' | 'created_at' | 'updated_at'> = {
          cupping_type: 'SCA',
          cupping_date: data.cuppingDate,
          roast_level: Number(data.roastLevel),
          is_qc_passed: data.qcPassed ?? undefined,
          defects: data.defects || [],
          type: entityType || 'LotType',
          score: finalScore,
          body: Number(data.body || 0),
          descriptors: '',
          fragrance: Number(data.fragrance || 0),
          aroma: Number(data.aroma || 0),
          fragrance_aroma: (Number(data.fragrance || 0) + Number(data.aroma || 0)), // Sum or keep separate? Standard payload has fragrance_aroma field.
          flavor: Number(data.flavor || 0),
          aftertaste: Number(data.aftertaste || 0),
          acidity: Number(data.acidity || 0),
          sweetness: Number(data.sweetness || 0),
          roast_defect_underdevelopment: Number(data.roast_defect_underdevelopment || 0),
          roast_defect_overdevelopment: Number(data.roast_defect_overdevelopment || 0),
          roast_defect_baked: Number(data.roast_defect_baked || 0),
          roast_defect_scorched: Number(data.roast_defect_scorched || 0),
          mouthfeel: Number(data.mouthfeel || 0),
          balance: Number(data.balance || 0),
          overall: Number(data.overall || 0),
          notes: [
            data.notes_fragrance && `Fragrance: ${data.notes_fragrance}`,
            data.notes_flavor && `Flavor: ${data.notes_flavor}`,
            data.notes_acidity && `Acidity: ${data.notes_acidity}`,
            data.notes_sweetness && `Sweetness: ${data.notes_sweetness}`,
            data.notes_mouthfeel && `Mouthfeel: ${data.notes_mouthfeel}`,
            data.notes_body && `Body: ${data.notes_body}`
          ].filter(Boolean).join('\n'),
          extended_data: {
            sca: {
              version: 'sca-2024-v2',
              raw_sum: aSum,
              non_uniform_cups: nonUniform,
              defective_cups: defective,
              penalty_non_uniform: nonUniform * 2,
              penalty_defects: defective * 4,
              final_score: finalScore,
              notes_fragrance: data.notes_fragrance,
              notes_flavor: data.notes_flavor,
              notes_acidity: data.notes_acidity,
              notes_sweetness: data.notes_sweetness,
              notes_mouthfeel: data.notes_mouthfeel,
              notes_body: data.notes_body,
            }
          }
        };

        if (sessionId) payload.cupping_session_id = sessionId;
        if (entityType === 'ItemType') payload.inventory_item_id = entityId;
        if (entityType === 'LotType') payload.lot_id = entityId || lotId;
        if (entityType === 'SampleType') {
          payload.sample_id = entityId;
          if (lotId) payload.lot_id = lotId;
        }

        // Always create new (profiles are non-editable)
        await profileService.createCuppingProfile(payload);
      } else {
        const prefix = isFullMode ? 'cupping_full' : 'cupping_simplified';
        const fragranceAromaAvg = (Number(data.fragrance) + Number(data.aroma)) / 2;
        const flavorScore = Number(data.flavor);
        const aftertasteScore = Number(data.aftertaste);

        const scores = {
          fragrance_aroma: Number(data.fragrance_aroma) || fragranceAromaAvg,
          flavor: flavorScore,
          aftertaste: aftertasteScore,
          acidity: Number(data.acidity),
          sweetness: Number(data.sweetness),
          mouthfeel: Number(data.mouthfeel),
          balance: Number(data.balance),
          overall: Number(data.overall),
        };

        const simplifiedScores = {
          fragrance_aroma: Number(data.fragrance_aroma),
          flavor: Number(data.flavor),
          aftertaste: Number(data.aftertaste),
          acidity: Number(data.acidity),
          sweetness: Number(data.sweetness),
          body: Number(data.body),
          balance: Number(data.balance),
          overall: 0,
        };

        const totalScore = mode === 'Simplified'
          ? Object.values(simplifiedScores).reduce((a, b) => a + b, 0)
          : Object.values(scores).reduce((a, b) => a + b, 0);

        const extendedData = {
          fragrance: Number(data.fragrance),
          aroma: Number(data.aroma),
          notes_fragrance: data.notes_fragrance,
          notes_flavor: data.notes_flavor,
          notes_acidity: data.notes_acidity,
          notes_sweetness: data.notes_sweetness,
          notes_mouthfeel: data.notes_mouthfeel,
          notes_body: data.notes_body,
          fragrance_notes_cata: data.fragrance_notes_cata,
          flavor_notes_cata: data.flavor_notes_cata,
          main_tastes: data.main_tastes,
          mouthfeel_attributes: data.mouthfeel_attributes,
          acidity_attributes: data.acidity_attributes,
        };

        const payload: Omit<CuppingProfile, 'id' | 'created_at' | 'updated_at'> = {
          cupping_type: mode,
          cupping_date: data.cuppingDate,
          roast_level: Number(data.roastLevel),
          is_qc_passed: data.qcPassed ?? undefined,
          defects: data.defects || [],
          type: entityType || 'LotType',
          score: totalScore,
          body: Number(data.body || 0),
          descriptors: '',
          fragrance: Number(data.fragrance),
          aroma: Number(data.aroma),
          mouthfeel: mode === 'Simplified' ? 0 : Number(data.mouthfeel || 0),
          roast_defect_underdevelopment: Number(data.roast_defect_underdevelopment || 0),
          roast_defect_overdevelopment: Number(data.roast_defect_overdevelopment || 0),
          roast_defect_baked: Number(data.roast_defect_baked || 0),
          roast_defect_scorched: Number(data.roast_defect_scorched || 0),
          ...(mode === 'Simplified' ? simplifiedScores : scores),
          notes: [
            data.notes_fragrance && `Fragrance: ${data.notes_fragrance}`,
            data.notes_flavor && `Flavor: ${data.notes_flavor}`,
            data.notes_acidity && `Acidity: ${data.notes_acidity}`,
            data.notes_sweetness && `Sweetness: ${data.notes_sweetness}`,
            data.notes_mouthfeel && `Mouthfeel: ${data.notes_mouthfeel}`,
            data.notes_body && `Body: ${data.notes_body}`
          ].filter(Boolean).join('\n'),
          extended_data: {
            [prefix]: extendedData
          },
        };

        if (sessionId) payload.cupping_session_id = sessionId;
        if (entityType === 'ItemType') payload.inventory_item_id = entityId;
        if (entityType === 'LotType') payload.lot_id = entityId || lotId;
        if (entityType === 'SampleType') {
          payload.sample_id = entityId;
          if (lotId) payload.lot_id = lotId;
        }

        // Always create new (profiles are non-editable)
        await profileService.createCuppingProfile(payload);
      }

      queryClient.invalidateQueries({ queryKey: ['cupping-profiles'] });
      onSuccess?.();
    } catch (error) {
      console.error("Failed to save cupping profile", error);
    }
  };

  return {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState,
    isFullMode,
    setIsFullMode: (v: boolean) => setMode(v ? 'Full' : 'Simplified'),
    mode,
    setMode,
    maxScore,
    scaComputedFinalScore,
    handleSave
  };
};
